/* eslint-disable import/order -- the `react-semantic-ui-datepickers` stylesheet is a
   SIDE-EFFECT import, and the ordering this rule asks for moves module imports across
   it. Stylesheet order is cascade order, so that is a change to what the page renders,
   not to its types. */
import type { Bundle } from 'fhir/r4';
import React from 'react';

import 'react-semantic-ui-datepickers/dist/react-semantic-ui-datepickers.css';
import { Tab } from 'semantic-ui-react';
import type { TabProps } from 'semantic-ui-react';

import type { ReferralSummary } from '../../../types/patient-data';
import type { PractitionerEntry, ResourceEntry } from '../../../utils/bundleNarrowing';

import ConnectNotesList from './connectNotesList';
import NotesList from './list';
import NotesListAll from './listAll';
import MemberDocuments from './memberDocuments';
import TasksNotesList from './tasksNotesList';

import type { SWRMutate } from './index';

/** The four keys `panes` below actually renders with. */
type NotesTabKey = 'ALL' | 'TASKS_NOTES' | 'CONNECT_NOTES' | 'MEMBER_DOCUMENTS';

/**
 * Transcribed from the producer: `notes/index.tsx` renders this with its own
 * `notesResponse` state, the `entry` arrays off the tasks/connect bundles, and
 * the three `useState` setters. `refresh*` are SWR mutators, passed straight
 * through to the list panes.
 */
export interface NotesTabsProps {
  style: React.CSSProperties;
  org: string;
  refresh: SWRMutate<Bundle | undefined>;
  refreshTasksNotes: SWRMutate<Bundle | undefined>;
  refreshConnectNotes: SWRMutate<Bundle | undefined>;
  practitionerData: PractitionerEntry[];
  /*
   * SUSPECTED DEFECT (pre-existing): the origin is `fhirPatient.id` at
   * `components/patient/index.tsx:531`, and FHIR R4 makes `Patient.id` optional,
   * so this is genuinely `string | undefined` here. Every note pane below
   * (`list`, `listAll`, `tasksNotesList`, `connectNotesList`, `memberDocuments`)
   * declares `patientId: string` and builds search URLs and note payloads from
   * it, so a Patient served without an `id` would post notes against
   * `patient=undefined`. Left `| undefined` because that is what the producer
   * sends; the panes are where the requirement is asserted.
   */
  patientId: string | undefined;
  notesData: ResourceEntry<'DocumentReference'>[];
  tasksNotesData: ResourceEntry<'Task'>[] | undefined;
  connectNotesData: ResourceEntry<'Communication'>[] | undefined;
  setNotesResponse: React.Dispatch<React.SetStateAction<ResourceEntry<'DocumentReference'>[]>>;
  setTasksNotesResponse: React.Dispatch<React.SetStateAction<never[]>>;
  setConnectNotesResponse: React.Dispatch<React.SetStateAction<never[]>>;
  allReferralData: ReferralSummary[] | undefined;
}

function Tabs(props: NotesTabsProps) {
  const [activeTab, setActiveTab] = React.useState(0);

  const showTabPane = (type: NotesTabKey) => {
    return (
      <Tab.Pane attached={false}>
        {/* The `!` on `patientId` at each pane below is what the SUSPECTED DEFECT note
            on that prop calls for — the panes are where the requirement is asserted.
            A Patient served without an `id` reaches them as `undefined` and their
            search URLs and note payloads carry it. */}
        {type !== 'TASKS_NOTES' && type !== 'ALL' && type !== 'CONNECT_NOTES' && type !== 'MEMBER_DOCUMENTS' && (
          <NotesList
            practitionerData={props.practitionerData}
            notesResponse={props.notesData}
            type={type}
            patientId={props.patientId!}
            refresh={props.refresh}
            setNotesResponse={props.setNotesResponse}
            org={props.org}
            allReferralData={props?.allReferralData}
          />
        )}
        {type === 'ALL' && (
          <NotesListAll
            practitionerData={props.practitionerData}
            notesResponse={props.notesData}
            type={type}
            patientId={props.patientId!}
            refresh={props.refresh}
            setNotesResponse={props.setNotesResponse}
            org={props.org}
            allReferralData={props?.allReferralData}
          />
        )}
        {type === 'TASKS_NOTES' && (
          <TasksNotesList
            practitionerData={props.practitionerData}
            notesResponse={props.tasksNotesData}
            type={type}
            patientId={props.patientId!}
            refreshTasksNotes={props.refreshTasksNotes}
            setNotesResponse={props.setTasksNotesResponse}
            org={props.org}
            allReferralData={props?.allReferralData}
          />
        )}
        {type === 'CONNECT_NOTES' && (
          <ConnectNotesList
            practitionerData={props.practitionerData}
            notes={props.connectNotesData}
            type={type}
            patientId={props.patientId!}
            refreshConnectNotes={props.refreshConnectNotes} //not used yet, placeholder value tasknoterefresher
            setConnectNotesResponse={props.setConnectNotesResponse}
            org={props.org}
          />
        )}
        {type === 'MEMBER_DOCUMENTS' && (
          <MemberDocuments patientId={props.patientId!} org={props.org} refresh={props.refresh} />
        )}
      </Tab.Pane>
    );
  };

  const panes = [
    // {
    //   menuItem: { key:"feedback", color: "teal", content: "Feedback", icon: "tasks" },
    //   render: () => showTabPane("FEEDBACK")
    // },
    // {
    //   menuItem: { key:"bullseye", color: "teal", content: "Actionable Insights", icon: "bullseye" },
    //   render: () => showTabPane("ACTIONABLE_INSIGHTS")

    // },
    // {
    //   menuItem: { key:'write', color: "teal", content: "General Notes", icon: "write" },
    //   render: () => showTabPane("GENERAL")
    // },
    {
      menuItem: { key: 'write', color: 'teal', content: 'General Notes', icon: 'tasks' },
      render: () => showTabPane('ALL'),
    },
    {
      menuItem: { key: 'tasks', color: 'teal', content: 'Tasks Notes', icon: 'tasks' },
      render: () => showTabPane('TASKS_NOTES'),
    },
    {
      menuItem: { key: 'connect', color: 'teal', content: 'Connect Notes', icon: 'tasks' },
      render: () => showTabPane('CONNECT_NOTES'),
    },
    {
      menuItem: { key: 'memberDocuments', color: 'teal', content: 'Member Documents', icon: 'file alternate outline' },
      render: () => showTabPane('MEMBER_DOCUMENTS'),
    },
  ];

  const handleTabChange = (e: React.MouseEvent<HTMLDivElement>, { activeIndex }: TabProps) => {
    setActiveTab(activeIndex as number);
  };

  return (
    <Tab
      menu={{ secondary: true, pointing: true }}
      activeIndex={activeTab}
      panes={panes}
      onTabChange={handleTabChange}
    />
  );
}

export default Tabs;
